<template>
  <div class="levels-page">
    <div class="page-header">
      <h2>关卡列表</h2>
      <div class="page-actions">
        <el-button type="warning" icon="el-icon-download" @click="handleExport" :loading="exporting">下载数据包</el-button>
        <el-button type="primary" @click="handleCreate">新建关卡</el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="levels"
      border
      style="width: 100%"
      row-key="id"
    >
      <el-table-column width="60" align="center">
        <template slot="header">
          <i class="el-icon-rank"></i>
        </template>
        <template>
          <i class="el-icon-rank drag-handler" style="cursor: move; color: #909399"></i>
        </template>
      </el-table-column>
      <el-table-column prop="id" label="ID" width="80" align="center" />
      <el-table-column label="图片" width="120" align="center">
        <template slot-scope="scope">
          <img
            v-if="scope.row.image_url"
            :src="resolveMediaUrl(scope.row.image_url)"
            style="width: 80px; height: 45px; object-fit: cover; border-radius: 4px; cursor: pointer;"
            @click="handleEdit(scope.row)"
          />
          <span v-else style="color: #c0c4cc;">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="关卡名称" min-width="150" />
      <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
      <el-table-column label="对应剧情" min-width="250">
        <template slot-scope="scope">
          <div class="story-plays-tags">
            <el-tag
              v-for="sp in scope.row.story_plays"
              :key="sp.id"
              size="small"
              class="sp-tag"
            >
              {{ sp.title }}
            </el-tag>
            <span v-if="!scope.row.story_plays || scope.row.story_plays.length === 0" style="color: #999">暂无</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button type="text" style="color: #F56C6C" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Create / Edit Dialog -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
    >
      <el-form label-width="100px" @submit.native.prevent>
        <el-form-item label="关卡名称" required>
          <el-input v-model="form.name" placeholder="请输入关卡名称" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入描述" />
        </el-form-item>
        <el-form-item label="图片">
          <div class="cover-uploader">
            <el-upload
              v-if="!form.image_url"
              :show-file-list="false"
              :http-request="handleImageUpload"
              action="#"
              accept="image/*"
              class="cover-upload-inner"
            >
              <i class="el-icon-plus cover-uploader-icon" />
            </el-upload>
            <img
              v-else
              :src="resolveMediaUrl(form.image_url)"
              class="cover-preview"
              @click="form.image_url = ''"
            />
          </div>
          <el-button
            v-if="form.image_url"
            size="mini"
            type="text"
            style="margin-top: 6px;"
            @click="form.image_url = ''"
          >
            移除图片
          </el-button>
        </el-form-item>
        <el-form-item label="对应剧情">
          <el-select
            v-model="form.story_play_ids"
            multiple
            filterable
            placeholder="请选择对应剧情"
            style="width: 100%"
          >
            <el-option
              v-for="item in availableStoryPlays"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getLevels,
  createLevel,
  updateLevel,
  deleteLevel,
  reorderLevels,
  exportLevels,
  getStoryPlays,
  uploadStoryImage
} from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";
import Sortable from "sortablejs";

export default {
  name: "Levels",
  data() {
    return {
      levels: [],
      availableStoryPlays: [],
      loading: false,
      exporting: false,
      dialogVisible: false,
      dialogTitle: "新建关卡",
      saving: false,
      form: {
        id: null,
        name: "",
        description: "",
        image_url: "",
        story_play_ids: []
      }
    };
  },
  created() {
    this.loadData();
    this.loadStoryPlays();
  },
  mounted() {
    this.initSortable();
  },
  methods: {
    resolveMediaUrl,
    async loadData() {
      this.loading = true;
      try {
        const { data } = await getLevels();
        this.levels = data.data || [];
      } catch (e) {
        this.$message.error("加载关卡列表失败");
      } finally {
        this.loading = false;
      }
    },
    async loadStoryPlays() {
      try {
        const { data } = await getStoryPlays({ per_page: 9999 });
        this.availableStoryPlays = data.items || data.list || data || [];
      } catch (e) {
        console.error("加载剧情列表失败", e);
      }
    },
    handleCreate() {
      this.form = {
        id: null,
        name: "",
        description: "",
        image_url: "",
        story_play_ids: []
      };
      this.dialogTitle = "新建关卡";
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.form = {
        id: row.id,
        name: row.name || "",
        description: row.description || "",
        image_url: row.image_url || "",
        story_play_ids: row.story_play_ids ? [...row.story_play_ids] : []
      };
      this.dialogTitle = "编辑关卡";
      this.dialogVisible = true;
    },
    async handleSave() {
      if (!this.form.name.trim()) {
        this.$message.warning("关卡名称不能为空");
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
          image_url: this.form.image_url,
          story_play_ids: this.form.story_play_ids
        };
        if (this.form.id) {
          await updateLevel(this.form.id, payload);
        } else {
          await createLevel(payload);
        }
        this.dialogVisible = false;
        this.$message.success("保存成功");
        this.loadData();
      } catch (e) {
        this.$message.error("保存失败");
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确定删除关卡「${row.name}」吗？`, "提示", { type: "warning" });
        await deleteLevel(row.id);
        this.$message.success("删除成功");
        this.loadData();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    async handleImageUpload({ file }) {
      try {
        const { data } = await uploadStoryImage(file);
        this.form.image_url = data.url;
        this.$message.success("图片上传成功");
      } catch (e) {
        this.$message.error("图片上传失败");
      }
    },
    initSortable() {
      this.$nextTick(() => {
        const el = document.querySelector('.levels-page .el-table__body-wrapper tbody');
        if (!el) return;
        this.sortable = Sortable.create(el, {
          handle: '.drag-handler',
          onEnd: evt => {
            const { oldIndex, newIndex } = evt;
            if (oldIndex === newIndex) return;
            
            const currRow = this.levels.splice(oldIndex, 1)[0];
            this.levels.splice(newIndex, 0, currRow);
            
            this.handleReorder();
          }
        });
      });
    },
    async handleReorder() {
      try {
        await reorderLevels({ ordered_ids: this.levels.map(l => l.id) });
        this.$message.success("排序已保存");
      } catch (e) {
        this.$message.error("排序保存失败");
        this.loadData();
      }
    },
    async handleExport() {
      this.exporting = true;
      try {
        const { data } = await exportLevels();
        const url = data.url;
        if (!url) {
          this.$message.error("下载失败：未生成下载链接");
          return;
        }
        const href = /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`;
        const a = document.createElement("a");
        a.href = href;
        a.rel = "noopener";
        const filename = data.filename || `levels_export.zip`;
        if (typeof a.download !== "undefined") {
          try { a.download = filename; } catch (_) { a.download = "export.zip"; }
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$message.success("数据包准备完毕，开始下载");
      } catch (e) {
        this.$message.error("下载数据包失败");
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.levels-page {
  padding: 20px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-header h2 {
  margin: 0;
}
.story-plays-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sp-tag {
  margin-bottom: 2px;
}
.cover-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  width: 160px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}
.cover-uploader:hover {
  border-color: #409eff;
}
.cover-upload-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
.cover-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
